import { ErrorMessage, SigninUserCommand, UserProxy } from '@/app/proxy/proxies';
import { Component, OnInit } from '@angular/core';
import { FormControl, FormGroup, ReactiveFormsModule, Validators } from '@angular/forms';
import { MessageService } from 'primeng/api';
import { AuthService } from '../services/auth.service';
import { Router } from '@angular/router';
import { environment } from '@/environments/environment';
import { PasswordValidationRules, PasswordValidators } from '../policy-validators/validators';
import { SharedModule } from '@/app/shared/shared.module';
import { LoadingService } from '@/app/core/services/loading.service';
import { LockScreenService } from '@/app/core/services/lock-screen.service';
import { PermissionService } from '@/app/core/services/permission.service';

@Component({
    selector: 'app-login',
    standalone: true,
    templateUrl: './login.component.html',
    styleUrl: './login.component.scss',
    imports: [SharedModule, ReactiveFormsModule],
    providers: [UserProxy],
})
export class LoginComponent implements OnInit {
    loginForm: FormGroup = new FormGroup({});

    forgetPasswordLink: string;

    constructor(
        private userProxy: UserProxy,
        private messageService: MessageService,
        private authDataService: AuthService,
        private router: Router,
        public loadingService: LoadingService,
        private lockScreenService: LockScreenService,
        private permissionService: PermissionService
    ) {
        this.userProxy.user_GetPasswordPolicy(environment.appId).subscribe((res) => {
            this.setValidations(res.result as PasswordValidationRules);
        });
    }

    ngOnInit(): void {
        this.loginForm = new FormGroup({
            email: new FormControl('', [Validators.required, Validators.email]),
            password: new FormControl('', [Validators.required, Validators.minLength(16)]),
        });

        const hostname = location.hostname;

        // If localhost → use doktar.cc
        if (hostname === 'localhost') {
            this.forgetPasswordLink = 'https://www.doktar.cc/auth/login/forgot-password';
        } else {
            let domain = hostname.replace('backoffice.', '');
            // doktar.io → doktar.app (backoffice.doktar.cc zaten doktar.cc olur)
            if (domain === 'doktar.io' || domain.endsWith('.doktar.io')) {
                domain = domain.replace(/\.?doktar\.io$/, 'doktar.app');
            }
            // backoffice.doktar.cc → doktar.cc ✓
            // backoffice.doktar.io → doktar.app ✓
            this.forgetPasswordLink = `https://www.${domain}/auth/login/forgot-password`;
        }
    }

    setValidations(validations: PasswordValidationRules): void {
        const passwordControl = this.loginForm.get('password');
        if (!passwordControl) {
            console.error('Password control not found in form');
            return;
        }

        passwordControl.setValidators(null);

        const passwordValidators = PasswordValidators.createPasswordValidators(validations);

        passwordValidators.unshift(Validators.required);

        passwordControl.setValidators(passwordValidators);

        passwordControl.updateValueAndValidity();
    }

    getPasswordErrorMessage(): string {
        const passwordControl = this.loginForm.get('password');

        if (!passwordControl || !passwordControl.errors) {
            return '';
        }

        if (passwordControl.hasError('required')) {
            return 'Password is required';
        }

        if (passwordControl.hasError('minlength')) {
            const requiredLength = passwordControl.getError('minlength').requiredLength;
            return `Password must be at least ${requiredLength} characters long`;
        }

        if (passwordControl.hasError('maxlength')) {
            const requiredLength = passwordControl.getError('maxlength').requiredLength;
            return `Password cannot exceed ${requiredLength} characters`;
        }

        if (passwordControl.hasError('insufficientUppercase')) {
            const { required } = passwordControl.getError('insufficientUppercase');
            return `Password must contain at least ${required} uppercase letter(s)`;
        }

        if (passwordControl.hasError('insufficientLowercase')) {
            const { required } = passwordControl.getError('insufficientLowercase');
            return `Password must contain at least ${required} lowercase letter(s)`;
        }

        if (passwordControl.hasError('insufficientNumbers')) {
            const { required } = passwordControl.getError('insufficientNumbers');
            return `Password must contain at least ${required} number(s)`;
        }

        if (passwordControl.hasError('noSpecialCharacters')) {
            return 'Password must contain at least one special character';
        }

        return 'Invalid password';
    }

    login() {
        let signin = new SigninUserCommand({
            email: this.loginForm.value.email,
            phoneCode: undefined,
            phone: undefined,
            password: this.loginForm.value.password,
            newPassword: undefined,
            captchaToken: undefined,
            externalProviderToken: undefined,
            externalProviderType: undefined,
        });

        this.userProxy.user_Signin(signin).subscribe(
            async (res) => {
                if (res && res.isSuccess === false) {
                    let errorMessage = 'Login failed';
                    if (res.errors && Array.isArray(res.errors)) {
                        errorMessage = res.errors.map((err: any) => err.message).join(', ');
                    } else if (res.result && (res.result as any)?.title) {
                        errorMessage = (res.result as any).title;
                    }
                    this.messageService.add({
                        severity: 'error',
                        summary: 'Error',
                        detail: errorMessage,
                    });
                    return;
                }
                this.messageService.add({
                    severity: 'success',
                    summary: 'Success',
                    detail: 'Login successful. Welcome back!',
                });
                this.authDataService.handleSigninResponse(res);
                this.lockScreenService.unLock(); // login sonrası lock state sıfırlansın
                await this.permissionService.getPermissions().toPromise();
                if (this.permissionService.hasPermission('user')) {
                    this.router.navigate(['/user/users']);
                } else {
                    this.router.navigate([this.permissionService.getFirstAccessiblePage()]);
                }
            },
            (error) => {
                let errorMessage = 'An error occurred during login';

                try {
                    // Try to parse the error response
                    const errorResponse = JSON.parse(error?.response || error?.error || '{}');

                    // Check if the response has the new format with errors array
                    if (errorResponse.errors && Array.isArray(errorResponse.errors)) {
                        errorMessage = errorResponse.errors.map((err: any) => err.message).join(', ');
                    }
                    // Fallback to old format if needed
                    else if (errorResponse.message) {
                        errorMessage = errorResponse.message;
                    }
                } catch (parseError) {
                    // If parsing fails, use a generic error message
                    console.error('Error parsing error response:', parseError);
                    errorMessage = 'Invalid credentials or server error';
                }

                this.messageService.add({
                    severity: 'error',
                    summary: 'Error',
                    detail: errorMessage,
                });
            }
        );
    }
}
